import { useEffect, useState } from "react";
import { fetchFeaturedBlogPosts } from "../../api/GlobalApi";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Pagination from "../../components/Pagination";
import { usePagination } from "../../hooks/usePagination";

function FeaturedBlogPosts () {

    const [author, setAuthor] = useState(null);
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Use pagination hook
    const {
        currentPage,
        totalPages,
        currentItems: currentPosts,
        indexOfFirstItem,
        indexOfLastItem,
        handlePageChange,
        totalItems
    } = usePagination(blogPosts, 20);

    useEffect(() => {
        const loadAuthorBlogs = async () => {
            try {
                const data = await fetchFeaturedBlogPosts();
                
                // Handle both formats
                if (Array.isArray(data)) {
                    setBlogPosts(data);
                    setAuthor({ name: "Featured Stories" });
                } else {
                    setAuthor(data.author || { name: "Featured Stories" });
                    setBlogPosts(data.blogs || []);
                }
                
                setLoading(false);
            } catch (error) {
                console.error("Error fetching blogs:", error);
                setLoading(false);
            }
        };

        loadAuthorBlogs();
    }, []);

    function formatDate(dateString) {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric"
        });
    }

    if (loading)
    return (
      <div style={styles.loaderContainer}>
        <div style={styles.spinner}></div>
      </div>
    );

    // Generate canonical URL with page number
    const canonicalUrl = currentPage > 1 
        ? `${window.location.origin}${window.location.pathname}?page=${currentPage}`
        : `${window.location.origin}${window.location.pathname}`;

    return (
        <>
            <Helmet>
                <title>{author.name} - Blogs - Play Store {currentPage > 1 ? `(Page ${currentPage})` : ''}</title>
                <meta name="description" content={`Play Store Blogs by ${author.name} ${currentPage > 1 ? `- Page ${currentPage}` : ''}`} />
                <meta name="keywords" content='All the tutorials and blogs are here' />
                <link rel="canonical" href={canonicalUrl} />
                
                {/* Pagination meta tags for SEO */}
                {currentPage > 1 && (
                    <link rel="prev" href={`${window.location.origin}${window.location.pathname}?page=${currentPage - 1}`} />
                )}
                {currentPage < totalPages && (
                    <link rel="next" href={`${window.location.origin}${window.location.pathname}?page=${currentPage + 1}`} />
                )}
                
                <meta property="og:title" content={`${author.name} - Blogs - Play Store`} />
                <meta property="og:description" content={"All the tutorials and blogs are here"} />
                <meta property="og:image" content={"appicon.png"} />
                <meta property="og:url" content={canonicalUrl} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${author.name} - Blogs - Play Store`} />
                <meta name="twitter:description" content={"All the tutorials and blogs are here"} />
                <meta name="twitter:image" content={"appicon.png"} />
            </Helmet>
            
            <main className="main-content">
                {/* Hero Section */}
                <section class="hero-section">
                    <div class="hero-card">
                        <div class="hero-content">
                            <h1>{author.name}</h1>
                            {totalItems > 0 && (
                                <p style={styles.postCount}>
                                    Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, totalItems)} of {totalItems} posts
                                    {currentPage > 1 && ` (Page ${currentPage})`}
                                </p>
                            )}
                        </div>
                    </div>
                </section>

                <section className="section">
                    <div className="games-grid">
                        {currentPosts.map((post) => (
                            <Link to={`/blog/${post.slug}`} key={post.id} className="game-card" style={{textDecoration: "none"}}>
                                <div className="game-image" style={{
                                    background: "linear-gradient(45deg, #ff9a9e, #fecfef)",
                                    backgroundImage: `url(${post.featured_image_url})`,
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    backgroundRepeat: "no-repeat",
                                }}>
                                    <div
                                        className="game-icon"
                                        style={{
                                        backgroundImage: `url(${post.avatar_url})`,
                                        backgroundSize: "cover",
                                        backgroundPosition: "center",
                                        }}
                                    ></div>
                                </div>
                                <div className="game-info">
                                    <div className="game-title">{post.title}</div>
                                    <div className="game-developer">{post.category} • {post.views_count} Views</div>
                                    <div className="game-rating">
                                        <span className="rating-text">{formatDate(post.updated_at)}</span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>

                    {/* Pagination Component */}
                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </section>
            </main>
        </>
    );
}

const styles = {
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  spinner: {
    width: "50px",
    height: "50px",
    border: "6px solid #ddd",
    borderTop: "6px solid #3498db",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  postCount: {
    fontSize: "14px",
    color: "#ffffffff",
    marginTop: "8px",
  },
};

// Add global keyframes for spinner
const styleSheet = document.styleSheets[0];
styleSheet.insertRule(
  `@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }`,
  styleSheet.cssRules.length
);

export default FeaturedBlogPosts;